import React from 'react';
import { Button, Modal, ModalHeader, ModalBody, ModalFooter, Row, Col } from 'reactstrap';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import PropTypes from 'prop-types';
import * as Yup from 'yup';
import { useFormik } from 'formik';

import { botVoiceAdd } from '../../../store/slices/tenantSlices/configurationSlice';
import FormInput from '../../../components/form/FormInput';
import FormFileUpload from '../../../components/form/FormFileUpload';

const AddBotVoiceModal = ({ isOpen, toggle, refreshList }) => {
  const dispatch = useDispatch();

  const initialValues = {
    name: '',
    description: '',
    default_style: '',
    reference_wav: null
  };

  const validationSchema = Yup.object({
    name: Yup.string().required('Name is required'),
    reference_wav: Yup.mixed().required('Reference audio file is required')
  });

  const onSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      const formData = new FormData();
      formData.append('name', values.name);
      formData.append('description', values.description);
      formData.append('default_style', values.default_style);
      if (values.reference_wav) {
        formData.append('reference_wav', values.reference_wav);
      }

      const response = await dispatch(botVoiceAdd(formData)).unwrap();
      if (response && response.success) {
        toast.success(response.message || 'Voice added successfully');
        resetForm();
        toggle();
        refreshList();
      } else {
        toast.error(response?.message || 'Failed to add voice');
      }
    } catch (err) {
      console.error('Error adding voice:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const {
    values,
    handleChange,
    handleBlur,
    handleSubmit,
    touched,
    errors,
    resetForm,
    setFieldValue,
    isSubmitting
  } = useFormik({
    initialValues,
    validationSchema,
    onSubmit
  });

  return (
    <Modal isOpen={isOpen} size="lg" centered>
      <ModalHeader className="justify-content-center py-2">
        <div className="fs-5">Add Bot Voice</div>
      </ModalHeader>
      <ModalBody className="px-4">
        <form id="bot-voice-form" onSubmit={handleSubmit}>
          <Row>
            <Col lg="6">
              <FormInput
                name="name"
                label="Name"
                placeholder="Enter Name"
                value={values.name}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
              />
            </Col>
            <Col lg="6">
              <FormInput
                name="default_style"
                label="Default Style"
                placeholder="e.g. gentle"
                value={values.default_style}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
              />
            </Col>
            <Col lg="12">
              <FormInput
                name="description"
                label="Description"
                placeholder="Enter Description"
                value={values.description}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
              />
            </Col>
            <Col lg="12">
              <FormFileUpload
                name="reference_wav"
                label="Reference Audio (WAV, FLAC, MP3, OPUS, WebM; 3–60s, ≥8kHz)"
                placeholder="Select audio file"
                accept=".wav,.flac,.mp3,.opus,.webm,audio/*"
                value={values.reference_wav}
                setFieldValue={setFieldValue}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
              />
            </Col>
          </Row>
        </form>
      </ModalBody>
      <ModalFooter>
        <Button
          onClick={() => {
            toggle();
            resetForm();
          }}
          className="px-5 bg-transparent text-primary"
          disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          form="bot-voice-form"
          type="submit"
          className="px-5 bg-primary border-primary"
          disabled={isSubmitting}>
          {isSubmitting ? 'Saving...' : 'Save'}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

AddBotVoiceModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  toggle: PropTypes.func.isRequired,
  refreshList: PropTypes.func.isRequired
};

export default AddBotVoiceModal;
